import {
  Alert,
  AlertActionCloseButton,
  EmptyStateBody,
  PageSection,
} from '@patternfly/react-core'
import * as React from 'react'

import type { IEntry } from '@app/domain/instrument/instrument.interface'
import { getVisitInstrumentPdfs } from '@app/domain/visit/visit.helpers'
import type { IVisitDefinition } from '@app/domain/visit/visit.interface'

import { InstrumentReports } from './instrument-reports'
import { VisitReports } from './visit-reports'

interface Props {
  definition: IVisitDefinition
  entries: Record<string, IEntry>
  getEntryPdf: (entryId: string, templateId: string) => void
  getVisitPdf: (templateId: string) => void
  isVisitPdfLoading: boolean
  isInstrumentPdfLoading: boolean
  downloadEntryPdfErrorMessage?: string
  downloadVisitPdfErrorMessage?: string
  onCloseDownloadEntryPdfError: () => void
  onCloseDownloadVisitPdfError: () => void
}

export const ReportsList = ({
  definition,
  entries,
  getEntryPdf,
  getVisitPdf,
  isVisitPdfLoading,
  isInstrumentPdfLoading,
  downloadEntryPdfErrorMessage,
  downloadVisitPdfErrorMessage,
  onCloseDownloadEntryPdfError,
  onCloseDownloadVisitPdfError,
}: Props) => {
  const errorMessage =
    downloadEntryPdfErrorMessage || downloadVisitPdfErrorMessage
  const onCloseError = downloadEntryPdfErrorMessage
    ? onCloseDownloadEntryPdfError
    : onCloseDownloadVisitPdfError

  const instrumentReports = React.useMemo(
    () => getVisitInstrumentPdfs(definition.allInstruments, entries),
    [definition.allInstruments, entries],
  )

  if (Object.keys(entries).length > 0) {
    return (
      <>
        <PageSection>
          {errorMessage && (
            <Alert
              variant="danger"
              title={errorMessage}
              aria-live="polite"
              isInline
              actionClose={<AlertActionCloseButton onClose={onCloseError} />}
            />
          )}
          <VisitReports
            isDownloading={isVisitPdfLoading}
            visitPdfs={definition.pdfTemplates}
            getVisitPdf={getVisitPdf}
          />
        </PageSection>
        <PageSection>
          <InstrumentReports
            isDownloading={isInstrumentPdfLoading}
            instrumentPdfs={instrumentReports}
            getEntryPdf={getEntryPdf}
          />
        </PageSection>
      </>
    )
  } else {
    return <EmptyStateBody>There are no reports available.</EmptyStateBody>
  }
}
